import { applyColumnClassifications, type ColumnClassificationMap } from './columnClassificationStore';
import type { DataSource, ModelTable, TableColumn } from './dataModelsTypes';

export function newId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export type SpaceOrderPlace = 'before' | 'after';

export function syncSpaceOrderIds(saved: string[], knownIds: string[]): string[] {
  const known = new Set(knownIds);
  const kept = saved.filter((id) => known.has(id));
  const keptSet = new Set(kept);
  return [...kept, ...knownIds.filter((id) => !keptSet.has(id))];
}

export function reorderSpaceIds(
  orderIds: string[],
  draggedId: string,
  targetId: string,
  place: SpaceOrderPlace,
): string[] {
  if (!draggedId || !targetId || draggedId === targetId) return orderIds;
  if (!orderIds.includes(draggedId) || !orderIds.includes(targetId)) return orderIds;
  const next = orderIds.filter((id) => id !== draggedId);
  const targetIndex = next.indexOf(targetId);
  if (targetIndex < 0) return orderIds;
  next.splice(place === 'before' ? targetIndex : targetIndex + 1, 0, draggedId);
  return next.every((id, index) => id === orderIds[index]) ? orderIds : next;
}

export function daysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

export function formatAbsolute(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function sourceTypeLabel(kind: DataSource['kind'], environment: string): string {
  if (kind === 'mongodb') return 'NoSQL';
  if (kind === 'snowflake' || kind === 'bigquery' || kind === 'databricks') {
    return environment === 'prod' ? 'Warehouse' : environment.replace(/^\w/, (ch) => ch.toUpperCase());
  }
  if (environment === 'crm') return 'CRM Database';
  return 'Database';
}

export function formatRelative(iso: string, now = Date.now()): string {
  const diff = Math.max(0, now - new Date(iso).getTime());
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return minutes <= 1 ? 'just now' : `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? '1 month ago' : `${months} months ago`;
}

export function formatRowCount(value: number): string {
  if (value >= 1_000_000) {
    const scaled = value / 1_000_000;
    return `${scaled % 1 === 0 ? scaled.toFixed(0) : scaled.toFixed(1)}M`;
  }
  if (value >= 1_000) {
    const scaled = value / 1_000;
    return `${scaled % 1 === 0 ? scaled.toFixed(0) : scaled.toFixed(1)}K`;
  }
  return String(value);
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} KB`;
  return `${bytes} B`;
}

export function sourceById(sources: DataSource[], id: string): DataSource | undefined {
  return sources.find((source) => source.id === id);
}

export function titleCaseTable(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

const COLUMN_TYPES = ['STRING', 'INTEGER', 'NUMBER', 'TIMESTAMP', 'BOOLEAN', 'JSON'] as const;

const FEATURED_COLUMNS: Record<string, TableColumn[]> = {
  customer_profile: [
    { name: 'customer_id', type: 'BIGINT', nullable: false, key: 'PK', pii: false, description: 'Customer identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'full_name', type: 'VARCHAR', nullable: false, key: '', pii: true, description: 'Customer full name', tags: ['pii', 'name'], classification: 'PII' },
    { name: 'email', type: 'VARCHAR', nullable: false, key: '', pii: true, description: 'Customer email', tags: ['pii', 'email'], classification: 'PII' },
    { name: 'phone', type: 'VARCHAR', nullable: true, key: '', pii: true, description: 'Customer phone', tags: ['pii', 'phone'], classification: 'PII' },
    { name: 'region', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Customer region', tags: ['region'], classification: 'Internal' },
    { name: 'tenant_id', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Tenant boundary', tags: ['tenant'], classification: 'Internal' },
    { name: 'account_manager_id', type: 'VARCHAR', nullable: true, key: 'FK', pii: false, description: 'Owning account manager', tags: ['manager'], classification: 'Internal' },
    { name: 'created_at', type: 'TIMESTAMP', nullable: false, key: '', pii: false, description: 'Profile created at', tags: ['audit'], classification: 'Internal' },
  ],
  customer_orders: [
    { name: 'order_id', type: 'BIGINT', nullable: false, key: 'PK', pii: false, description: 'Order identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'customer_id', type: 'BIGINT', nullable: false, key: 'FK', pii: false, description: 'Customer identifier', tags: ['customer'], classification: 'Internal' },
    { name: 'order_amount', type: 'DECIMAL', nullable: false, key: '', pii: false, description: 'Order amount', tags: ['financial'], classification: 'Confidential' },
    { name: 'region', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Order region', tags: ['region'], classification: 'Internal' },
    { name: 'tenant_id', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Tenant boundary', tags: ['tenant'], classification: 'Internal' },
    { name: 'order_date', type: 'DATE', nullable: false, key: '', pii: false, description: 'Order date', tags: ['orderdate'], classification: 'Internal' },
    { name: 'account_manager_id', type: 'VARCHAR', nullable: true, key: 'FK', pii: false, description: 'Owning account manager', tags: ['manager'], classification: 'Internal' },
  ],
  customer_contacts: [
    { name: 'contact_id', type: 'VARCHAR', nullable: false, key: 'PK', pii: false, description: 'Contact identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'customer_id', type: 'BIGINT', nullable: false, key: 'FK', pii: false, description: 'Customer identifier', tags: ['customer'], classification: 'Internal' },
    { name: 'email', type: 'VARCHAR', nullable: false, key: '', pii: true, description: 'Contact email', tags: ['pii', 'email'], classification: 'PII' },
    { name: 'phone', type: 'VARCHAR', nullable: true, key: '', pii: true, description: 'Contact phone', tags: ['pii', 'phone'], classification: 'PII' },
    { name: 'channel', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Preferred channel', tags: ['channel'], classification: 'Internal' },
    { name: 'region', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Contact region', tags: ['region'], classification: 'Internal' },
    { name: 'tenant_id', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Tenant boundary', tags: ['tenant'], classification: 'Internal' },
  ],
  customer_transactions: [
    { name: 'transaction_id', type: 'VARCHAR', nullable: false, key: 'PK', pii: false, description: 'Unique transaction identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'customer_id', type: 'BIGINT', nullable: false, key: 'FK', pii: false, description: 'Customer identifier', tags: ['customer'], classification: 'Internal' },
    { name: 'order_id', type: 'BIGINT', nullable: true, key: 'FK', pii: false, description: 'Associated order id', tags: ['order'], classification: 'Internal' },
    { name: 'amount', type: 'DECIMAL', nullable: false, key: '', pii: false, description: 'Transaction amount', tags: ['financial'], classification: 'Confidential' },
    { name: 'currency', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Transaction currency', tags: ['financial'], classification: 'Internal' },
    { name: 'region', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Transaction region', tags: ['region'], classification: 'Internal' },
    { name: 'tenant_id', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Tenant boundary', tags: ['tenant'], classification: 'Internal' },
    { name: 'card_last4', type: 'VARCHAR', nullable: true, key: '', pii: true, description: 'Card last four', tags: ['pci', 'payment'], classification: 'PCI' },
  ],
  marketing_preferences: [
    { name: 'preference_id', type: 'VARCHAR', nullable: false, key: 'PK', pii: false, description: 'Preference identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'customer_id', type: 'BIGINT', nullable: false, key: 'FK', pii: false, description: 'Customer identifier', tags: ['customer'], classification: 'Internal' },
    { name: 'email', type: 'VARCHAR', nullable: false, key: '', pii: true, description: 'Marketing email', tags: ['pii', 'email'], classification: 'PII' },
    { name: 'channel', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Preferred channel', tags: ['channel'], classification: 'Internal' },
    { name: 'opt_in', type: 'BOOLEAN', nullable: false, key: '', pii: false, description: 'Consent flag', tags: ['consent'], classification: 'Internal' },
    { name: 'region', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Preference region', tags: ['region'], classification: 'Internal' },
    { name: 'tenant_id', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Tenant boundary', tags: ['tenant'], classification: 'Internal' },
  ],
  employees: [
    { name: 'employee_id', type: 'VARCHAR', nullable: false, key: 'PK', pii: false, description: 'Employee identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'employee_name', type: 'VARCHAR', nullable: false, key: '', pii: true, description: 'Employee name', tags: ['pii', 'name'], classification: 'PII' },
    { name: 'email', type: 'VARCHAR', nullable: false, key: '', pii: true, description: 'Employee email', tags: ['pii', 'email'], classification: 'PII' },
    { name: 'manager_id', type: 'VARCHAR', nullable: true, key: 'FK', pii: false, description: 'Manager identifier', tags: ['manager'], classification: 'Internal' },
    { name: 'department', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Department', tags: ['department'], classification: 'Internal' },
    { name: 'region', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Employee region', tags: ['region'], classification: 'Internal' },
  ],
  employee_hierarchy: [
    { name: 'manager_id', type: 'VARCHAR', nullable: false, key: 'FK', pii: false, description: 'Manager identifier', tags: ['manager'], classification: 'Internal' },
    { name: 'employee_id', type: 'VARCHAR', nullable: false, key: 'FK', pii: false, description: 'Employee identifier', tags: ['identifier'], classification: 'Internal' },
  ],
  departments: [
    { name: 'department_id', type: 'VARCHAR', nullable: false, key: 'PK', pii: false, description: 'Department identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'department', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Department name', tags: ['department'], classification: 'Internal' },
    { name: 'region', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Department region', tags: ['region'], classification: 'Internal' },
  ],
  employee_salaries: [
    { name: 'employee_id', type: 'VARCHAR', nullable: false, key: 'FK', pii: false, description: 'Employee identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'salary', type: 'DECIMAL', nullable: false, key: '', pii: false, description: 'Base salary', tags: ['compensation'], classification: 'Confidential' },
    { name: 'bonus', type: 'DECIMAL', nullable: true, key: '', pii: false, description: 'Bonus amount', tags: ['compensation'], classification: 'Confidential' },
    { name: 'currency', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Pay currency', tags: ['financial'], classification: 'Internal' },
    { name: 'effective_date', type: 'DATE', nullable: false, key: '', pii: false, description: 'Effective date', tags: ['audit'], classification: 'Internal' },
  ],
  payroll_records: [
    { name: 'employee_id', type: 'VARCHAR', nullable: false, key: 'FK', pii: false, description: 'Employee identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'gross_salary', type: 'DECIMAL', nullable: false, key: '', pii: false, description: 'Gross salary', tags: ['payroll'], classification: 'Restricted' },
    { name: 'bank_account', type: 'VARCHAR', nullable: false, key: '', pii: true, description: 'Bank account number', tags: ['bank', 'restricted'], classification: 'Restricted' },
    { name: 'payment_date', type: 'DATE', nullable: false, key: '', pii: false, description: 'Payment date', tags: ['audit'], classification: 'Internal' },
  ],
  invoices: [
    { name: 'invoice_id', type: 'VARCHAR', nullable: false, key: 'PK', pii: false, description: 'Invoice identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'account_id', type: 'VARCHAR', nullable: false, key: 'FK', pii: false, description: 'Account identifier', tags: ['account'], classification: 'Internal' },
    { name: 'amount', type: 'DECIMAL', nullable: false, key: '', pii: false, description: 'Invoice amount', tags: ['financial'], classification: 'Confidential' },
    { name: 'region', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Invoice region', tags: ['region'], classification: 'Internal' },
    { name: 'due_date', type: 'DATE', nullable: false, key: '', pii: false, description: 'Due date', tags: ['audit'], classification: 'Internal' },
  ],
  payments: [
    { name: 'payment_id', type: 'VARCHAR', nullable: false, key: 'PK', pii: false, description: 'Payment identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'invoice_id', type: 'VARCHAR', nullable: false, key: 'FK', pii: false, description: 'Invoice identifier', tags: ['invoice'], classification: 'Internal' },
    { name: 'amount', type: 'DECIMAL', nullable: false, key: '', pii: false, description: 'Payment amount', tags: ['financial'], classification: 'Confidential' },
    { name: 'card_pan', type: 'VARCHAR', nullable: true, key: '', pii: true, description: 'Card primary account number', tags: ['pci'], classification: 'PCI' },
    { name: 'paid_at', type: 'TIMESTAMP', nullable: false, key: '', pii: false, description: 'Payment timestamp', tags: ['audit'], classification: 'Internal' },
  ],
  financial_accounts: [
    { name: 'account_id', type: 'VARCHAR', nullable: false, key: 'PK', pii: false, description: 'Account identifier', tags: ['identifier'], classification: 'Internal' },
    { name: 'account_name', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Account name', tags: ['name'], classification: 'Internal' },
    { name: 'region', type: 'VARCHAR', nullable: false, key: '', pii: false, description: 'Account region', tags: ['region'], classification: 'Internal' },
    { name: 'balance', type: 'DECIMAL', nullable: false, key: '', pii: false, description: 'Account balance', tags: ['financial'], classification: 'Confidential' },
  ],
};

export function columnsForTable(table: ModelTable, overrides?: ColumnClassificationMap): TableColumn[] {
  const featured = FEATURED_COLUMNS[table.name];
  const extra = (
    index: number,
    name: string,
    options: { key?: TableColumn['key']; pii?: boolean; tags?: string[]; nullable?: boolean; description?: string } = {},
  ): TableColumn => ({
    name,
    type: COLUMN_TYPES[index % COLUMN_TYPES.length],
    nullable: options.nullable ?? index % 3 === 0,
    key: options.key ?? '',
    pii: options.pii ?? false,
    description: options.description ?? `Additional attribute ${index + 1}`,
    tags: options.tags ?? ['attribute'],
  });
  const columns: TableColumn[] = featured
    ? [
        ...featured,
        ...Array.from({ length: Math.max(0, table.columns - featured.length) }, (_, index) => extra(index, `attr_${index + 1}`)),
      ].slice(0, table.columns)
    : Array.from({ length: table.columns }, (_, index) => {
        const bases = table.name.replace(/s$/, '').split('_');
        const prefix = bases[bases.length - 1] || 'field';
        const name = index === 0 ? `${prefix}_id` : index === 1 ? 'created_at' : `${prefix}_${index}`;
        return extra(index, name, {
          key: index === 0 ? 'PK' : index === 2 ? 'FK' : '',
          pii: /email|phone|name|address|contact/i.test(table.name) && index % 4 === 2,
          tags: index === 0 ? ['identifier'] : ['attribute'],
          nullable: index > 2 && index % 3 === 0,
          description: `Column ${index + 1} on ${table.name}`,
        });
      });
  return applyColumnClassifications(columns, table.id, overrides);
}
